import { jwtDecode } from "jwt-decode";
import { getAccessToken, getSessionExpiry } from "./authStorage.js";
import { store } from "../App/store.js";

export const isValidJWT = (token) => {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.exp * 1000 > Date.now();
    } catch (e) {
        return false;
    }
};

/**
 * Best-guess "is this user still in a valid session?" check, used by the
 * App.js gatekeeper before any API call has had a chance to run.
 *
 * The access token now lives in memory only and is null on every page load
 * until the cookie-based silent refresh fetches a new one. So checking the
 * access token here would always return false and bounce the user to /login.
 *
 * Instead, treat the user as authenticated when BOTH:
 *   (a) the persisted user object exists in Redux  (rehydrated from storage)
 *   (b) the session expiry timestamp is still in the future
 *
 * If (a) and (b) hold, the HttpOnly refresh cookie is presumed still valid
 * and the 401 interceptor will silently fetch a fresh access token on the
 * first protected API call. If the cookie is actually gone, the very first
 * API call will fail to refresh and the interceptor calls doLogout() — so
 * we degrade gracefully without bouncing legitimate users.
 */
export const isAuthenticated = () => {
    // If we already have a non-expired access token in memory, fast-path yes.
    const token = getAccessToken();
    if (token) {
        try {
            const decoded = jwtDecode(token);
            if (decoded.exp > Date.now() / 1000) return true;
        } catch {/* fall through to session check */}
    }

    // Otherwise check the persisted session metadata.
    const user = store.getState()?.auth?.user;
    if (!user) return false;

    const expiryStr = getSessionExpiry();
    if (!expiryStr) return false;

    const expiryMs = new Date(expiryStr).getTime();
    return Number.isFinite(expiryMs) && expiryMs > Date.now();
};
